var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import '../../styles/widgets/popup/fractionTilesManipulativePopup.scss';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { t } from 'i18next';
import RadioButton from '../../components/RadioButton';
import FooterActionsButtons from './FooterActionsButtons';
import { parseFraction, getFractionDimensions, polarToCartesian, describeArc, getFractionCircleConfig, sumAndReduceFractions, } from '../../helper/helper';
import { Fraction } from '../../components/MathJax';
import { FRACTION_CONFIG } from '../../constants/fractionConfig';
import FractionTileMathEditor from '../FractionTileMathEditor';
import globalStore from '../../store';
import { DataContext } from '../../player/DataContext';
import CustomScrollbar from '../../components/CustomScrollbar';
var shapeSize = {
    rectangle: {
        width: 480,
        height: 55,
    },
    circle: {
        width: 300,
        height: 300,
    },
};
var DEFAULT_MATH_EDITOR_WIDTH = 118;
/** Returns a plain-text alternative for a fraction string (e.g. "1/2", "3"). */
var getFractionAriaLabel = function (fractionSize) {
    var parsed = parseFraction(fractionSize);
    if (!parsed)
        return fractionSize;
    if (parsed.denominator === 0)
        return String(parsed.numerator);
    return "".concat(parsed.numerator, "/").concat(parsed.denominator);
};
var FractionTilesManipulativePopup = function (props) {
    var editDetails = props.editDetails, onSubmit = props.onSubmit, onCancel = props.onCancel;
    var SHAPES_OPTS = [
        t('text.rectanglesTiles', { defaultValue: 'Rectangles Tiles' }),
        t('text.circlesTiles', { defaultValue: 'Circles Tiles' }),
    ];
    var LABLES_OPTS = [
        t('text.labeled', { defaultValue: 'Labeled' }),
        t('text.customLabel', { defaultValue: 'Custom Label' }),
    ];
    var bottomToolConfig = globalStore.useStoreState(function (store) { return store.jsonDataStore; }).bottomToolConfig;
    var _a = useContext(DataContext), selectedTool = _a.selectedTool, setSelectedTool = _a.setSelectedTool, manipulative = _a.manipulative;
    var initialTargetTiles = useState(function () {
        var selectedFractions = manipulative
            .filter(function (item) {
            var itemId = item.data.id || '';
            return (selectedTool.some(function (t) { return itemId === t.replace(/_custom_label_(rect|circle)_\d+$/, ''); }) &&
                item.data.type === 'fractionTiles');
        })
            .map(function (item) { return item.data; });
        return selectedFractions.length > 0 ? selectedFractions : editDetails ? [editDetails] : [];
    })[0];
    var initialSelectedIds = useState(function () { return initialTargetTiles.map(function (tile) { return tile.id; }).filter(Boolean); })[0];
    var _b = useState({
        shapeType: (editDetails === null || editDetails === void 0 ? void 0 : editDetails.shapeType) || 'rectangle',
        lablesType: (editDetails === null || editDetails === void 0 ? void 0 : editDetails.lablesType) || 'labeled',
        customLabel: (editDetails === null || editDetails === void 0 ? void 0 : editDetails.customLabel) || '',
        individualLabels: {},
    }), state = _b[0], setState = _b[1];
    var scrollContainerRef = useRef(null);
    var _c = useState(false), isOverflowing = _c[0], setIsOverflowing = _c[1];
    // WCAG 4.1.3 – live-region announcements for shape/label type changes
    var _d = useState(''), announcement = _d[0], setAnnouncement = _d[1];
    var prevShapeTypeRef = useRef(state.shapeType);
    var prevLablesTypeRef = useRef(state.lablesType);
    useEffect(function () {
        var checkOverflow = function () {
            if (scrollContainerRef.current) {
                var hasOverflow = scrollContainerRef.current.scrollWidth > scrollContainerRef.current.clientWidth;
                setIsOverflowing(hasOverflow);
            }
        };
        checkOverflow();
        window.addEventListener('resize', checkOverflow);
        var observer = new ResizeObserver(checkOverflow);
        if (scrollContainerRef.current) {
            observer.observe(scrollContainerRef.current);
        }
        return function () {
            window.removeEventListener('resize', checkOverflow);
            observer.disconnect();
        };
    }, [initialTargetTiles, state.shapeType]);
    useEffect(function () {
        if (initialTargetTiles.length > 0) {
            setState(function (prev) { return (__assign(__assign({}, prev), { individualLabels: initialTargetTiles.reduce(function (acc, tile) {
                    acc[tile.id] = tile.customLabel || '';
                    return acc;
                }, {}) })); });
        }
    }, [initialTargetTiles]);
    var handleDone = function () {
        var config = getFractionDimensions(currentFractionSize);
        var dimensions = state.shapeType === 'rectangle'
            ? (config === null || config === void 0 ? void 0 : config.rectangle) || shapeSize.rectangle
            : shapeSize.circle;
        onSubmit === null || onSubmit === void 0 ? void 0 : onSubmit(__assign(__assign({}, state), { dimensions: dimensions, fractionSize: currentFractionSize, selectedIds: initialSelectedIds, isMultiple: initialSelectedIds.length > 1 }));
    };
    var isDisableDoneBtn = useMemo(function () {
        var disabled = false;
        return disabled;
    }, [state]);
    var isSmallTile = useMemo(function () {
        return initialTargetTiles.some(function (tile) {
            var fractionSize = tile.gluedFrom && tile.gluedFrom.length > 0
                ? sumAndReduceFractions(tile.gluedFrom.map(function (f) { return f.fractionSize; }))
                : tile.fractionSize;
            var config = getFractionDimensions(fractionSize);
            if (!config)
                return false;
            if (state.shapeType === 'rectangle') {
                return config.rectangle.width < 32;
            }
            else {
                return config.circle.sweep < 20;
            }
        });
    }, [initialTargetTiles, state.shapeType]);
    var currentFractionSize = useMemo(function () {
        var firstTile = initialTargetTiles[0];
        var fractionSize = (firstTile === null || firstTile === void 0 ? void 0 : firstTile.fractionSize) || 'fraction-1';
        if ((firstTile === null || firstTile === void 0 ? void 0 : firstTile.gluedFrom) && firstTile.gluedFrom.length > 0) {
            var gluedFractionStrings = firstTile.gluedFrom.map(function (tile) { return tile.fractionSize; });
            fractionSize = sumAndReduceFractions(gluedFractionStrings);
        }
        return fractionSize;
    }, [initialTargetTiles]);
    // const labelTitle = useMemo(() => {
    //   if (state.lablesType === 'custom') return state.customLabel;
    //   const parsed = parseFraction(currentFractionSize);
    //   return parsed ? (
    //     <div key={`mathjax-${currentFractionSize}`} className="fraction-popup-mathjax-container">
    //       <Fraction
    //         numerator={parsed.numerator}
    //         denominator={parsed.denominator}
    //         integer={parsed.denominator === 0 ? parsed.numerator : undefined}
    //       />
    //     </div>
    //   ) : null;
    // }, [state.lablesType, state.customLabel, currentFractionSize]);
    var groupedCircleTiles = useMemo(function () {
        if (state.shapeType !== 'circle')
            return [];
        var groups = {};
        initialTargetTiles.forEach(function (tile) {
            var key = "".concat(tile.center.x, ",").concat(tile.center.y);
            if (!groups[key])
                groups[key] = [];
            groups[key].push(tile);
        });
        return Object.values(groups).map(function (group) {
            return {
                id: group[0].id,
                constituents: group,
            };
        });
    }, [initialTargetTiles, state.shapeType]);
    var groupedRectTiles = useMemo(function () {
        if (state.shapeType !== 'rectangle')
            return [];
        var tolerance = 5;
        var sortedTiles = __spreadArray([], initialTargetTiles, true).sort(function (a, b) { return a.center.y - b.center.y || a.center.x - b.center.x; });
        var groups = [];
        sortedTiles.forEach(function (tile) {
            var _a;
            var addedToGroup = false;
            for (var _i = 0, groups_1 = groups; _i < groups_1.length; _i++) {
                var group = groups_1[_i];
                // Only check tiles in the same approximate Y row
                if (Math.abs(group[0].center.y - tile.center.y) < tolerance) {
                    // Check if this tile snaps to the right of any tile in the group
                    // Usually, tiles are sorted, so we can just check against the last tile in the group
                    var lastTileInGroup = group[group.length - 1];
                    var lastFractionSize = lastTileInGroup.gluedFrom && lastTileInGroup.gluedFrom.length > 0
                        ? sumAndReduceFractions(lastTileInGroup.gluedFrom.map(function (f) { return f.fractionSize; }))
                        : lastTileInGroup.fractionSize;
                    var lastTileConfig = getFractionDimensions(lastFractionSize);
                    var lastTileWidth = ((_a = lastTileConfig === null || lastTileConfig === void 0 ? void 0 : lastTileConfig.rectangle) === null || _a === void 0 ? void 0 : _a.width) || 660;
                    if (Math.abs(lastTileInGroup.center.x + lastTileWidth - tile.center.x) < tolerance) {
                        group.push(tile);
                        addedToGroup = true;
                        break;
                    }
                }
            }
            if (!addedToGroup) {
                groups.push([tile]);
            }
        });
        return groups.map(function (group, index) { return ({
            id: group[0].id || "rect-group-".concat(index),
            constituents: group,
        }); });
    }, [initialTargetTiles, state.shapeType]);
    useEffect(function () {
        if (prevShapeTypeRef.current !== state.shapeType) {
            prevShapeTypeRef.current = state.shapeType;
            setAnnouncement(state.shapeType === 'rectangle'
                ? t('text.rectanglesTiles', { defaultValue: 'Rectangle Tiles selected' })
                : t('text.circlesTiles', { defaultValue: 'Circle Tiles selected' }));
        }
    }, [state.shapeType]);
    useEffect(function () {
        if (prevLablesTypeRef.current !== state.lablesType) {
            prevLablesTypeRef.current = state.lablesType;
            setAnnouncement(state.lablesType === 'labeled'
                ? t('text.labeled', { defaultValue: 'Labeled selected' })
                : t('text.customLabel', { defaultValue: 'Custom Label selected' }));
        }
    }, [state.lablesType]);
    // Circle Helper Functions
    var editorSelectionIds = useMemo(function () {
        var ids = [];
        if (initialTargetTiles.length > 0) {
            if (state.shapeType === 'rectangle') {
                groupedRectTiles.forEach(function (group, groupIndex) {
                    group.constituents.forEach(function (tile, index) {
                        var _a;
                        var fractionSize = tile.gluedFrom && tile.gluedFrom.length > 0
                            ? sumAndReduceFractions(tile.gluedFrom.map(function (f) { return f.fractionSize; }))
                            : tile.fractionSize;
                        var config = getFractionDimensions(fractionSize);
                        var exactWidth = Math.min(440, ((_a = config === null || config === void 0 ? void 0 : config.rectangle) === null || _a === void 0 ? void 0 : _a.width) || 440);
                        if (exactWidth >= 32 && state.lablesType === 'custom') {
                            ids.push("fractionPopupRect-".concat(groupIndex, "-").concat(index, "_").concat(groupIndex * 100 + index + 10));
                        }
                    });
                });
            }
            else {
                groupedCircleTiles.forEach(function (group, index) {
                    group.constituents.forEach(function (tile, tidx) {
                        var fractionSize = tile.gluedFrom && tile.gluedFrom.length > 0
                            ? sumAndReduceFractions(tile.gluedFrom.map(function (f) { return f.fractionSize; }))
                            : tile.fractionSize;
                        var config = getFractionCircleConfig(fractionSize, tile.rotation || 0, tile.customSweep);
                        var isSmall = config.sweep < 20;
                        if (state.lablesType === 'custom' && !isSmall) {
                            ids.push("fractionPopupCircle-".concat(index, "-").concat(tidx, "_").concat(index * 10 + tidx));
                        }
                    });
                });
            }
        }
        else {
            if (state.shapeType === 'rectangle' && state.lablesType === 'custom') {
                ids.push("fractionPopupPreviewRect_2");
            }
            else if (state.shapeType === 'circle' && state.lablesType === 'custom') {
                ids.push("fractionPopupPreviewCircle_3");
            }
        }
        return ids;
    }, [groupedRectTiles, groupedCircleTiles, state.shapeType, state.lablesType, initialTargetTiles]);
    var handleEditorKeyDown = function (e, currentSelectionId) {
        if (e.key === 'Tab') {
            e.preventDefault();
            var currentIndex = editorSelectionIds.indexOf(currentSelectionId);
            if (currentIndex !== -1 && editorSelectionIds.length > 1) {
                var shift = 1;
                if ('shiftKey' in e && e.shiftKey) {
                    shift = -1;
                }
                var nextIndex_1 = (currentIndex + shift + editorSelectionIds.length) % editorSelectionIds.length;
                setSelectedTool([editorSelectionIds[nextIndex_1]]);
                if (isOverflowing && scrollContainerRef.current) {
                    setTimeout(function () {
                        var _a;
                        var editorContainers = (_a = scrollContainerRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('.fraction-popup-editor-container');
                        var nextContainer = editorContainers === null || editorContainers === void 0 ? void 0 : editorContainers[nextIndex_1];
                        if (nextContainer) {
                            nextContainer.scrollIntoView({
                                behavior: 'smooth',
                                block: 'nearest',
                                inline: 'center',
                            });
                        }
                    }, 0);
                }
            }
        }
    };
    var handleTextChange = function (content) {
        var value = Array.isArray(content)
            ? content
            : content
                ? [{ type: 'text', value: String(content) }]
                : [];
        setState(function (prev) {
            var newState = __assign(__assign({}, prev), { customLabel: value });
            return newState;
        });
    };
    var handleIndividualTextChange = function (content, tileId) {
        var value = Array.isArray(content)
            ? content
            : content
                ? [{ type: 'text', value: String(content) }]
                : [];
        setState(function (prev) {
            var _a;
            return __assign(__assign({}, prev), { individualLabels: __assign(__assign({}, prev.individualLabels), (_a = {}, _a[tileId] = value, _a)) });
        });
    };
    var renderCircleShape = function () {
        return (_jsx("div", { className: "fraction-popup-circle-shape-wrapper", children: groupedCircleTiles.map(function (group, index) {
                var constituents = group.constituents;
                var radius = 150;
                var center = 150;
                // Build a plain-text label describing all fraction slices in this circle group
                var circleGroupAriaLabel = constituents
                    .map(function (tile) {
                    var fs = tile.gluedFrom && tile.gluedFrom.length > 0
                        ? sumAndReduceFractions(tile.gluedFrom.map(function (f) { return f.fractionSize; }))
                        : tile.fractionSize;
                    return getFractionAriaLabel(fs);
                })
                    .join(', ');
                return (_jsxs("div", { className: "fraction-popup-circle-shape-container", role: "img", "aria-label": t('text.fractionCircleGroup', {
                        defaultValue: "Fraction circle: ".concat(circleGroupAriaLabel),
                    }), children: [_jsx("svg", { width: "100%", height: "100%", viewBox: "0 0 300 300", className: "fraction-popup-svg", "aria-hidden": "true", focusable: "false", children: constituents.map(function (tile, tidx) {
                                var rotation = tile.rotation || 0;
                                var customSweep = tile.customSweep;
                                var fractionSize = tile.gluedFrom && tile.gluedFrom.length > 0
                                    ? sumAndReduceFractions(tile.gluedFrom.map(function (f) { return f.fractionSize; }))
                                    : tile.fractionSize;
                                var config = getFractionCircleConfig(fractionSize, rotation, customSweep);
                                var isFullCircle = config.sweep === 360;
                                var commonProps = {
                                    fill: 'white',
                                    stroke: '#293273',
                                    strokeWidth: '0.1875rem',
                                    strokeLinejoin: 'round',
                                    strokeLinecap: 'round',
                                };
                                return isFullCircle ? (_jsx("circle", __assign({ cx: center, cy: center, r: radius }, commonProps), "".concat(tile.id, "-").concat(tidx))) : (_jsx("path", __assign({ d: describeArc(center, center, radius, config.start, config.start + config.sweep) }, commonProps), "".concat(tile.id, "-").concat(tidx)));
                            }) }), constituents.map(function (tile, tidx) {
                            var _a, _b;
                            var rotation = tile.rotation || 0;
                            var customSweep = tile.customSweep;
                            var fractionSize = tile.gluedFrom && tile.gluedFrom.length > 0
                                ? sumAndReduceFractions(tile.gluedFrom.map(function (f) { return f.fractionSize; }))
                                : tile.fractionSize;
                            var config = getFractionCircleConfig(fractionSize, rotation, customSweep);
                            var isFullCircle = config.sweep === 360;
                            var midAngle = config.start + config.sweep / 2;
                            var isGlued = !!(tile.gluedFrom && tile.gluedFrom.length > 0);
                            var labelRadius = 0;
                            if (!isFullCircle) {
                                var alpha_1 = (config.sweep / 2) * (Math.PI / 180);
                                var safeAlpha = alpha_1 === 0 ? 1 : alpha_1;
                                labelRadius = (2 * radius * Math.sin(safeAlpha)) / (3 * safeAlpha);
                                // Adjust label radius for glued tiles to avoid vertex overlap
                                if (isGlued && config.sweep > 180 && labelRadius < 35) {
                                    labelRadius = 35;
                                }
                            }
                            var labelPos = polarToCartesian(center, center, labelRadius, midAngle);
                            var alpha = (config.sweep / 2) * (Math.PI / 180);
                            var chordWidth = 2 * labelRadius * Math.sin(alpha);
                            var editorWidth = Math.min(DEFAULT_MATH_EDITOR_WIDTH, isFullCircle ? DEFAULT_MATH_EDITOR_WIDTH : chordWidth * 0.82);
                            if (isGlued) {
                                editorWidth = Math.max(60, editorWidth);
                            }
                            var dynamicMaxWidth = "".concat(Math.max(20, editorWidth) / 16, "rem");
                            var labelLocalPos = {
                                x: "".concat((labelPos.x / 300) * 100, "%"),
                                y: "".concat((labelPos.y / 300) * 100, "%"),
                            };
                            var parsed = parseFraction(fractionSize);
                            var currentLabelTitle = state.lablesType === 'custom' ? (((_a = state.individualLabels[tile.id]) !== null && _a !== void 0 ? _a : state.customLabel)) : parsed ? (_jsx("div", { className: "fraction-popup-mathjax-container", children: _jsx(Fraction, { numerator: parsed.numerator, denominator: parsed.denominator, integer: parsed.denominator === 0 ? parsed.numerator : undefined }) }, "mathjax-".concat(fractionSize, "-").concat(index, "-").concat(tidx))) : null;
                            if (config.sweep < 20)
                                return null;
                            var absoluteTextAngle = (midAngle + 90) % 360;
                            if (absoluteTextAngle < 0)
                                absoluteTextAngle += 360;
                            var finalRotation = absoluteTextAngle;
                            var roundedAngle = Math.round(absoluteTextAngle);
                            if (roundedAngle > 90 && roundedAngle < 270) {
                                finalRotation += 180;
                            }
                            if ((parsed && parsed.denominator === 0) || isGlued) {
                                finalRotation = 0;
                            }
                            var editorId = "fractionPopupCircle-".concat(index, "-").concat(tidx, "_").concat(index * 10 + tidx);
                            var isSelected = selectedTool.includes(editorId);
                            return (_jsx("div", { className: "fraction-popup-label-wrapper", style: {
                                    '--label-x': labelLocalPos.x,
                                    '--label-y': labelLocalPos.y,
                                    transform: state.lablesType === 'labeled'
                                        ? "translate(-50%, -50%) rotate(".concat(finalRotation, "deg)")
                                        : 'translate(-50%, -50%)',
                                    zIndex: isSelected ? 10 : 1,
                                }, children: state.lablesType === 'custom' ? (_jsx("div", { className: "fraction-popup-editor-container", inert: true, style: {
                                        '--editor-width': dynamicMaxWidth,
                                        '--placeholder-font-size': "".concat(Math.min(1, Math.max(0.6, editorWidth / 80)), "rem"),
                                    }, children: _jsx(FractionTileMathEditor, { data: {
                                            type: 'data',
                                            data: {
                                                text: (_b = state.individualLabels[tile.id]) !== null && _b !== void 0 ? _b : state.customLabel,
                                                color: '#293273',
                                                alignment: 'center',
                                                size: 25,
                                            },
                                        }, className: "fraction-popup-input-default", idPreffix: "fractionPopupCircle-".concat(index, "-").concat(tidx), index: index * 10 + tidx, keypadButtons: bottomToolConfig.mathKeypad, bottomBtns: bottomToolConfig.mathKeypadBottom, disableTransform: true, isManipulative: true, isTextUpdate: false, viewonly: true, cb: function (val) { return handleIndividualTextChange(val, tile.id); }, onKeyDown: function (e) {
                                            return handleEditorKeyDown(e, "fractionPopupCircle-".concat(index, "-").concat(tidx, "_").concat(index * 10 + tidx));
                                        }, throwChangeOnBlur: true, placeHolder: isSelected ? t('text.typeHere', { defaultValue: 'Type here' }) : '​', dataPlaceholder: isSelected ? t('text.typeHere', { defaultValue: 'Type here' }) : '​' }) })) : (_jsx("span", { className: "fraction-popup-default-label", "aria-label": getFractionAriaLabel(fractionSize), style: {
                                        '--font-size': FRACTION_CONFIG[fractionSize]
                                            ? '1.563rem'
                                            : "".concat(1 +
                                                0.5625 *
                                                    Math.min(1, Math.max(0, (config.sweep - 30) / (180 - 30))), "rem"),
                                    }, children: currentLabelTitle })) }, "label-".concat(tile.id, "-").concat(index, "-").concat(tidx)));
                        })] }, "circle-shape-group-".concat(group.id, "-").concat(index)));
            }) }));
    };
    var renderRectangleShape = function () {
        return (_jsx("div", { className: "fraction-popup-rect-shape-wrapper", children: groupedRectTiles.map(function (group, groupIndex) {
                return (_jsx("div", { className: "fraction-popup-rect-group-container", children: group.constituents.map(function (tile, index) {
                        var _a, _b, _c;
                        var fractionSize = tile.gluedFrom && tile.gluedFrom.length > 0
                            ? sumAndReduceFractions(tile.gluedFrom.map(function (f) { return f.fractionSize; }))
                            : tile.fractionSize;
                        var config = getFractionDimensions(fractionSize);
                        var exactWidth = Math.min(440, ((_a = config === null || config === void 0 ? void 0 : config.rectangle) === null || _a === void 0 ? void 0 : _a.width) || 440);
                        var isLast = index === group.constituents.length - 1;
                        var rectEditorId = "fractionPopupRect-".concat(groupIndex, "-").concat(index, "_").concat(groupIndex * 100 + index + 10);
                        var isSelected = selectedTool.includes(rectEditorId);
                        var parsed = parseFraction(fractionSize);
                        var currentLabelTitle = state.lablesType === 'custom' ? (((_b = state.individualLabels[tile.id]) !== null && _b !== void 0 ? _b : state.customLabel)) : parsed ? (_jsx("div", { className: "fraction-popup-mathjax-container", children: _jsx(Fraction, { numerator: parsed.numerator, denominator: parsed.denominator, integer: parsed.denominator === 0 ? parsed.numerator : undefined }) }, "mathjax-".concat(fractionSize, "-").concat(index))) : null;
                        return (_jsx("div", { className: "fraction-popup-rect-shape-container ".concat(!isLast ? 'fraction-popup-rect-no-right' : ''), "aria-label": t('text.fractionRectTile', {
                                defaultValue: "Fraction rectangle: ".concat(getFractionAriaLabel(fractionSize)),
                            }), style: {
                                '--rect-width': "".concat(exactWidth, "px"),
                                position: 'relative',
                            }, children: exactWidth >= 32 &&
                                (state.lablesType === 'custom' ? (_jsx("div", { className: "fraction-popup-editor-container", inert: true, style: {
                                        '--editor-width': "".concat(Math.min(DEFAULT_MATH_EDITOR_WIDTH, exactWidth - 12) / 16, "rem"),
                                        '--placeholder-font-size': "".concat(Math.min(1, Math.max(0.6, exactWidth / 80)), "rem"),
                                    }, children: _jsx(FractionTileMathEditor, { data: {
                                            type: 'data',
                                            data: {
                                                text: (_c = state.individualLabels[tile.id]) !== null && _c !== void 0 ? _c : state.customLabel,
                                                color: '#293273',
                                                alignment: 'center',
                                                size: 25,
                                            },
                                        }, className: "fraction-popup-input-default", idPreffix: "fractionPopupRect-".concat(groupIndex, "-").concat(index), index: groupIndex * 100 + index + 10, keypadButtons: bottomToolConfig.mathKeypad, bottomBtns: bottomToolConfig.mathKeypadBottom, disableTransform: true, isManipulative: true, isTextUpdate: false, viewonly: true, cb: function (val) { return handleIndividualTextChange(val, tile.id); }, onKeyDown: function (e) {
                                            return handleEditorKeyDown(e, "fractionPopupRect-".concat(groupIndex, "-").concat(index, "_").concat(groupIndex * 100 + index + 10));
                                        }, throwChangeOnBlur: true, placeHolder: isSelected ? t('text.typeHere', { defaultValue: 'Type here' }) : '​', dataPlaceholder: isSelected ? t('text.typeHere', { defaultValue: 'Type here' }) : '​' }) })) : (_jsx("span", { className: "fraction-popup-default-label", "aria-label": getFractionAriaLabel(fractionSize), style: {
                                        '--font-size': FRACTION_CONFIG[fractionSize]
                                            ? '1.5625rem'
                                            : "".concat(1 +
                                                0.5625 *
                                                    Math.min(1, Math.max(0, (exactWidth - 55) / (165 - 55))), "rem"),
                                    }, children: currentLabelTitle }))) }, "".concat(tile.id, "-").concat(index)));
                    }) }, "rect-group-".concat(group.id, "-").concat(groupIndex)));
            }) }));
    };
    return (_jsxs("div", { className: "fractionTilesManipulative", role: "region", "aria-label": t('text.fractionTilesManipulationUI', {
            defaultValue: 'Fraction Tiles Manipulation UI',
        }), tabIndex: 0, onPointerDown: function (e) { return e.stopPropagation(); }, onMouseDown: function (e) { return e.stopPropagation(); }, children: [_jsx("div", { role: "status", "aria-live": "polite", "aria-atomic": "true", style: {
                    position: 'absolute',
                    width: '1px',
                    height: '1px',
                    padding: 0,
                    margin: '-1px',
                    overflow: 'hidden',
                    clip: 'rect(0,0,0,0)',
                    whiteSpace: 'nowrap',
                    borderWidth: 0,
                }, children: announcement }), _jsxs("div", { className: "scrollbarHolder", children: [_jsx(CustomScrollbar, { direction: "vertical", tabIndex: -1, verticalTrackAriaHidden: true, children: _jsxs("div", { style: { margin: 'auto 0' }, children: [_jsx("div", { className: "fraction-popup-preview-container", children: _jsx("div", { className: "fraction-popup-scroll-wrapper ".concat(isOverflowing ? 'is-overflowing' : ''), ref: scrollContainerRef, tabIndex: isOverflowing ? 0 : undefined, "aria-label": isOverflowing
                                            ? t('text.fractionTilesScrollable', {
                                                defaultValue: 'Fraction tiles preview, scroll horizontally to see more',
                                            })
                                            : undefined, children: initialTargetTiles.length > 0 ? (state.shapeType === 'rectangle' ? (renderRectangleShape()) : (renderCircleShape())) : state.shapeType === 'rectangle' ? (_jsx("div", { className: "fraction-popup-rect-preview", role: "img", "aria-label": t('text.fractionRectPreview', {
                                                defaultValue: 'Fraction rectangle preview: 1 whole',
                                            }), children: state.lablesType === 'custom' ? (_jsx("div", { className: "fraction-popup-editor-container", inert: true, style: {
                                                    '--editor-width': "".concat(DEFAULT_MATH_EDITOR_WIDTH / 16, "rem"),
                                                    '--placeholder-font-size': '1rem',
                                                }, children: _jsx(FractionTileMathEditor, { data: {
                                                        type: 'data',
                                                        data: {
                                                            text: state.customLabel,
                                                            color: '#293273',
                                                            alignment: 'center',
                                                            size: 25,
                                                        },
                                                    }, className: "fraction-popup-input-default", idPreffix: "fractionPopupPreviewRect", index: 2, keypadButtons: bottomToolConfig.mathKeypad, bottomBtns: bottomToolConfig.mathKeypadBottom, disableTransform: true, isManipulative: true, isTextUpdate: false, viewonly: true, cb: handleTextChange, onKeyDown: function (e) { return handleEditorKeyDown(e, "fractionPopupPreviewRect_2"); }, throwChangeOnBlur: true, placeHolder: selectedTool.includes('fractionPopupPreviewRect_2')
                                                        ? t('text.typeHere', { defaultValue: 'Type here' })
                                                        : '​', dataPlaceholder: selectedTool.includes('fractionPopupPreviewRect_2')
                                                        ? t('text.typeHere', { defaultValue: 'Type here' })
                                                        : '​' }) })) : (_jsx("span", { className: "fraction-popup-default-label", children: "1" })) })) : (_jsx("div", { className: "fraction-popup-circle-preview", role: "img", "aria-label": t('text.fractionCirclePreview', {
                                                defaultValue: 'Fraction circle preview: 1 whole',
                                            }), children: state.lablesType === 'custom' ? (_jsx("div", { className: "fraction-popup-editor-container", inert: true, style: {
                                                    '--editor-width': "".concat(DEFAULT_MATH_EDITOR_WIDTH / 16, "rem"),
                                                    '--placeholder-font-size': '1rem',
                                                }, children: _jsx(FractionTileMathEditor, { data: {
                                                        type: 'data',
                                                        data: {
                                                            text: state.customLabel,
                                                            color: '#293273',
                                                            alignment: 'center',
                                                            size: 25,
                                                        },
                                                    }, className: "fraction-popup-input-default", idPreffix: "fractionPopupPreviewCircle", index: 3, keypadButtons: bottomToolConfig.mathKeypad, bottomBtns: bottomToolConfig.mathKeypadBottom, disableTransform: true, isManipulative: true, isTextUpdate: false, viewonly: true, cb: handleTextChange, onKeyDown: function (e) { return handleEditorKeyDown(e, "fractionPopupPreviewCircle_3"); }, throwChangeOnBlur: true, placeHolder: selectedTool.includes('fractionPopupPreviewCircle_3')
                                                        ? t('text.typeHere', { defaultValue: 'Type here' })
                                                        : '​', dataPlaceholder: selectedTool.includes('fractionPopupPreviewCircle_3')
                                                        ? t('text.typeHere', { defaultValue: 'Type here' })
                                                        : '​' }) })) : (_jsx("span", { className: "fraction-popup-default-label", children: "1" })) })) }) }), _jsx("div", { className: "settings-container", children: _jsxs("div", { className: "settings", children: [_jsxs("div", { className: "settings-row", role: "group", "aria-labelledby": "shapes-group-label", children: [_jsx("span", { id: "shapes-group-label", className: "settings-title", children: t('text.shapes', { defaultValue: 'Shapes' }) }), _jsx(RadioButton, { id: "shapes", name: "shapes", options: SHAPES_OPTS, selected: state.shapeType === 'rectangle' ? 0 : 1, onChange: function (value) {
                                                            setState(function (prev) { return (__assign(__assign({}, prev), { shapeType: value === 0 ? 'rectangle' : 'circle' })); });
                                                        }, className: "radioGroup" })] }), _jsxs("div", { className: "settings-row", role: "group", "aria-labelledby": "labels-group-label", children: [_jsx("span", { id: "labels-group-label", className: "settings-title", children: t('text.labels', { defaultValue: 'Labels' }) }), _jsx(RadioButton, { id: "labels", name: "labels", options: LABLES_OPTS, selected: state.lablesType === 'labeled' ? 0 : 1, onChange: function (value) {
                                                            setState(function (prev) { return (__assign(__assign({}, prev), { lablesType: value === 0 ? 'labeled' : 'custom' })); });
                                                        }, className: "radioGroup" })] })] }) })] }) }), _jsx(FooterActionsButtons, { disableDoneBtn: isDisableDoneBtn, onActionBtnClick: function (type) {
                            if (type === 'done') {
                                handleDone();
                            }
                            else {
                                onCancel === null || onCancel === void 0 ? void 0 : onCancel();
                            }
                        } })] })] }));
};
export default FractionTilesManipulativePopup;
